import { Component, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface FileError {
  file: File;
  reason: string;
}

@Component({
  selector: 'app-ai-search-bar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ai-search-bar.component.html',
  styleUrls: ['./ai-search-bar.component.scss']
})
export class AiSearchBarComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('textInput') textInput!: ElementRef<HTMLInputElement>;

  @Input() placeholder: string = 'Ask anything ...';
  @Input() value: string = '';
  @Input() isRecording: boolean = false;
  @Input() attachedFiles: File[] = [];

  @Output() valueChange = new EventEmitter<string>();
  @Output() search = new EventEmitter<string>();
  @Output() attachFile = new EventEmitter<void>();
  @Output() voiceInput = new EventEmitter<void>();
  @Output() filesChanged = new EventEmitter<File[]>();
  @Output() fileError = new EventEmitter<FileError>();

  private readonly ALLOWED_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx', 'pdf', 'jpg', 'png', 'gif', 'jpeg'];
  private readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes

  get acceptedFileTypes(): string {
    return this.ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',');
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.valueChange.emit(this.value);
  }

  onSubmit(): void {
    if (this.value.trim() || this.attachedFiles.length > 0) {
      this.search.emit(this.value);
    }
  }

  onAttachFile(): void {
    this.fileInput.nativeElement.click();
    this.attachFile.emit();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validationError = this.validateFile(file);

        if (validationError) {
          this.fileError.emit({ file, reason: validationError });
        } else {
          // Check if file is not already attached (by name and size)
          const isDuplicate = this.attachedFiles.some(
            f => f.name === file.name && f.size === file.size
          );
          if (!isDuplicate) {
            this.attachedFiles = [...this.attachedFiles, file];
          }
        }
      }
      this.filesChanged.emit(this.attachedFiles);
    }

    // Reset the input so the same file can be selected again if removed
    input.value = '';
  }

  private validateFile(file: File): string | null {
    // Check file extension
    const fileName = file.name.toLowerCase();
    const extension = fileName.split('.').pop() || '';

    if (!this.ALLOWED_EXTENSIONS.includes(extension)) {
      return `File type ".${extension}" is not supported. Allowed types: ${this.ALLOWED_EXTENSIONS.join(', ')}`;
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `File size (${sizeMB}MB) exceeds the 50MB limit`;
    }

    return null;
  }

  removeFile(index: number): void {
    this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
    this.filesChanged.emit(this.attachedFiles);
  }

  onVoiceInput(): void {
    this.voiceInput.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSubmit();
    }
  }

  focusInput(): void {
    this.textInput?.nativeElement.focus();
  }
}
